import { useEffect, useRef, ReactNode } from "react";
import { Box, Portal } from "@chakra-ui/react-v3";
import type { BoxProps } from "@chakra-ui/react-v3";
import { acquireScrollLock, releaseScrollLock } from "../utils/overlay-scroll-lock";

type SimpleModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onFocusOutside?: (event: FocusEvent) => void;
  maxWidth?: string;
  maxHeight?: string;
  backdropZIndex?: number;
  contentZIndex?: number;
  children: ReactNode;
};

type ModalHeaderProps = BoxProps & {
  children: ReactNode;
};

type ModalBodyProps = BoxProps & {
  children: ReactNode;
};

type ModalFooterProps = BoxProps & {
  children: ReactNode;
};

function ModalHeader({ children, ...props }: ModalHeaderProps) {
  return (
    <Box p="16px" borderBottom="1px solid #e5e7eb" flexShrink={0} {...props}>
      {children}
    </Box>
  );
}

function ModalBody({ children, ...props }: ModalBodyProps) {
  return (
    <Box p="16px" flex={1} overflowY="auto" {...props}>
      {children}
    </Box>
  );
}

function ModalFooter({ children, ...props }: ModalFooterProps) {
  return (
    <Box p="16px" borderTop="1px solid #e5e7eb" flexShrink={0} {...props}>
      {children}
    </Box>
  );
}

function SimpleModalRoot({
  isOpen,
  onClose,
  onFocusOutside,
  maxWidth = "1400px",
  maxHeight = "95vh",
  backdropZIndex = 1400,
  contentZIndex = 1401,
  children,
}: SimpleModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);
  const previousActiveElement = useRef<Element | null>(null);

  useEffect(() => {
    if (isOpen) {
      previousActiveElement.current = document.activeElement;
      acquireScrollLock();

      setTimeout(() => {
        modalRef.current?.focus();
      }, 0);

      return () => {
        releaseScrollLock();
      };
    } else {
      if (previousActiveElement.current instanceof HTMLElement) {
        previousActiveElement.current.focus();
      }
      return;
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        onClose();
      }
    }

    function handleFocusOut(event: FocusEvent) {
      if (onFocusOutside && modalRef.current && !modalRef.current.contains(event.target as Node)) {
        onFocusOutside(event);
      }
    }

    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("focusout", handleFocusOut, true);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("focusout", handleFocusOut, true);
    };
  }, [isOpen, onClose, onFocusOutside]);

  if (!isOpen) return null;

  return (
    <Portal>
      <Box
        position="fixed"
        top={0}
        left={0}
        right={0}
        bottom={0}
        bg="rgba(0, 0, 0, 0.6)"
        zIndex={backdropZIndex}
        onClick={onClose}
      />
      <Box
        position="fixed"
        top={0}
        left={0}
        right={0}
        bottom={0}
        display="flex"
        alignItems="center"
        justifyContent="center"
        zIndex={contentZIndex}
        pointerEvents="none"
      >
        <Box
          ref={modalRef}
          role="dialog"
          aria-modal="true"
          tabIndex={-1}
          position="relative"
          pointerEvents="auto"
          bg="white"
          borderRadius="12px"
          boxShadow="0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)"
          outline="none"
          maxWidth={maxWidth}
          maxHeight={maxHeight}
          display="flex"
          flexDirection="column"
          width="90vw"
        >
          {children}
        </Box>
      </Box>
    </Portal>
  );
}

export const SimpleModal = Object.assign(SimpleModalRoot, {
  Header: ModalHeader,
  Body: ModalBody,
  Footer: ModalFooter,
});
